import React from 'react';
import { Box, Typography } from '@mui/material';
import AddIcon from '@mui/icons-material/Add';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import { nanoid } from 'nanoid';
import {
  FeedbackQuestion,
  FeedbackQuestionType,
  getFeedbackTypeLabel
} from '../../../../../../../../components/admin3/newProgram/masterclass/types';

const sectionBoxSx = {
  border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
  borderRadius: '12px',
  background: 'rgba(255,255,255,0.02)',
  p: 2.5
} as const;

const FEEDBACK_TYPES: FeedbackQuestionType[] = ['text', 'list', 'rating', 'yesno', 'roles'];

interface FeedbackQuestionsSectionProps {
  questions: FeedbackQuestion[];
  onChange: (questions: FeedbackQuestion[]) => void;
}

/**
 * Editor for a lesson's mentee feedback questions (`menteeQuestions`). Kept in the
 * post-creation design builder because stage-6's LessonContentView doesn't cover
 * it. Edits live in the builder's lesson state and persist on "Save Changes"
 * (mapped back to `menteeQuestions` by buildTemplateLevelsPayload).
 */
const FeedbackQuestionsSection: React.FC<FeedbackQuestionsSectionProps> = ({ questions, onChange }) => {
  const update = (id: string, patch: Partial<FeedbackQuestion>) =>
    onChange(questions.map((q) => (q.id === id ? { ...q, ...patch } : q)));

  const remove = (id: string) => onChange(questions.filter((q) => q.id !== id));

  const add = () => onChange([...questions, { id: nanoid(), prompt: '', type: 'text' }]);

  return (
    <Box sx={sectionBoxSx}>
      <Box
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: 1,
          mb: 1.5,
          flexWrap: 'wrap'
        }}
      >
        <Box>
          <Typography
            sx={{ fontSize: 15, fontWeight: 600, fontFamily: 'Inter', color: 'var(--Text-text-tertiary, #868686)' }}
          >
            Survey
          </Typography>
          <Typography
            sx={{
              fontSize: 12,
              fontFamily: 'Inter',
              color: 'var(--Text-text-secondary, #B6B6B6)',
              lineHeight: 1.5,
              mt: 0.5
            }}
          >
            Optional questions the learner answers after completing this lesson.
          </Typography>
        </Box>
        <Typography
          component="span"
          onClick={add}
          sx={{
            fontSize: 13,
            fontFamily: 'Inter',
            color: 'var(--Stroke-highlight, #F79420)',
            cursor: 'pointer',
            display: 'inline-flex',
            alignItems: 'center',
            gap: 0.25
          }}
        >
          <AddIcon sx={{ fontSize: 16 }} /> Add
        </Typography>
      </Box>

      {questions.length === 0 ? (
        <Typography
          sx={{ fontSize: 12, fontFamily: 'Inter', color: 'var(--Text-text-secondary, #B6B6B6)', fontStyle: 'italic' }}
        >
          No Survey questions for this lesson.
        </Typography>
      ) : (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 1.5 }}>
          {questions.map((q, idx) => (
            <Box
              key={q.id}
              sx={{
                background: '#1A1A1A',
                border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                borderRadius: '8px',
                p: 2
              }}
            >
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.5 }}>
                <Box
                  component="input"
                  value={q.prompt}
                  onChange={(e: React.ChangeEvent<HTMLInputElement>) => update(q.id, { prompt: e.target.value })}
                  placeholder={`Question ${idx + 1}`}
                  sx={{
                    flex: 1,
                    minWidth: 0,
                    background: '#0F0F0F',
                    border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                    borderRadius: '6px',
                    px: 1.5,
                    py: 1,
                    color: 'var(--Text-text-primary, #FDFDFD)',
                    fontFamily: 'Inter',
                    fontSize: 13,
                    outline: 'none',
                    '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
                  }}
                />
                <Box
                  component="select"
                  value={q.type}
                  onChange={(e: React.ChangeEvent<HTMLSelectElement>) =>
                    update(q.id, { type: e.target.value as FeedbackQuestionType })
                  }
                  sx={{
                    background: '#0F0F0F',
                    border: '1px solid var(--Stroke-Border-border-dim, #2A2A2A)',
                    borderRadius: '6px',
                    px: 1,
                    py: 1,
                    color: 'var(--Text-text-primary, #FDFDFD)',
                    fontFamily: 'Inter',
                    fontSize: 12,
                    outline: 'none',
                    cursor: 'pointer',
                    '&:focus': { borderColor: 'var(--Stroke-highlight, #F79420)' }
                  }}
                >
                  {FEEDBACK_TYPES.map((t) => (
                    <option key={t} value={t} style={{ background: '#1A1A1A' }}>
                      {getFeedbackTypeLabel(t)}
                    </option>
                  ))}
                </Box>
                <DeleteOutlineIcon
                  onClick={() => remove(q.id)}
                  sx={{
                    fontSize: 18,
                    color: 'var(--Text-text-tertiary, #868686)',
                    cursor: 'pointer',
                    flexShrink: 0,
                    '&:hover': { color: 'var(--UI-Error, #F44336)' }
                  }}
                />
              </Box>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
};

export default FeedbackQuestionsSection;
